import React from "react";
import TopNav from "../../components/TopNav.jsx";
import SiteFooter from "../../components/SiteFooter.jsx";
import { Link } from "react-router-dom";
import { Calculator, Search, Check, FileText, ArrowRight, SlidersHorizontal } from "lucide-react";

const DEFAULT_HERO_SRC = "/Sourcing_FINAL.png";

const defaultFeatureIcons = [Calculator, SlidersHorizontal, Check];
const defaultStepIcons = [Calculator, Search, FileText];

/**
 * Shared layout for "coming soon" product calculator pages.
 * Pass product-specific copy via props — do not use generic BTL-only text here.
 */
export default function ProductComingSoonLanding({
  productLabel,
  heroTitle,
  heroDescription,
  heroSrc = DEFAULT_HERO_SRC,
  primaryCtaText,
  /** Pills under hero (after product name + Coming soon) */
  heroTags = ["Lender-led matching", "Fast product sourcing"],
  /** Section under hero: main intro */
  introTitle,
  introBody,
  /** Three feature cards */
  featureCards,
  /** "How it works" column */
  howItWorksTitle,
  howItWorksIntro,
  steps,
  /** Right column: criteria / filters preview */
  criteriaTitle = "Typical lender criteria (preview)",
  criteriaIntro,
  criteriaChips,
  /** Bottom CTA band */
  ctaTitle,
  ctaDescription,
  ctaPrimary = { to: "/sourcing/buy-to-let-mortgage-sourcing", label: "Start with BTL today" },
  ctaSecondary = { to: "/explore-borrower-journey", label: "Explore borrower journey" },
  /** When set, hero primary CTA is a live link (e.g. invoice finance sourcing) instead of a disabled “coming soon” button. */
  primaryCtaTo,
}) {
  const cards = featureCards?.length === 3 ? featureCards : null;
  const stepList = steps?.length ? steps : [];
  const chips = criteriaChips?.length ? criteriaChips : [];

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = heroSrc;
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, [heroSrc]);

  return (
    <main className="min-h-screen bg-white text-slate-800">
      <TopNav />

      <section className="relative h-[520px] w-full overflow-hidden hero-landscape-responsive">
        <img
          src={heroSrc}
          alt={`${productLabel} coming soon`}
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-sky-950/55" />
        <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center text-white">
          <h1 className="text-4xl font-black tracking-tight md:text-6xl">{heroTitle}</h1>
          <p className="mt-4 max-w-3xl text-base text-white/90 md:text-lg">{heroDescription}</p>

          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row">
            {primaryCtaTo ? (
              <Link
                to={primaryCtaTo}
                className="inline-flex items-center gap-2 rounded-lg bg-sky-500 px-6 py-3 text-lg font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-sky-600 hover:shadow-lg"
              >
                {primaryCtaText} <ArrowRight className="h-4 w-4" />
              </Link>
            ) : (
              <button
                type="button"
                disabled
                className="inline-flex items-center gap-2 rounded-lg bg-sky-500 px-6 py-3 text-lg font-semibold text-white transition-all opacity-70 cursor-not-allowed"
              >
                {primaryCtaText}
              </button>
            )}

            <Link
              to="/explore-borrower-journey"
              className="inline-flex items-center gap-2 rounded-lg bg-sky-500 px-6 py-3 text-lg font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-sky-600 hover:shadow-lg"
            >
              Explore Borrower Journey <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-sm text-white/90">
            <span className="rounded-full bg-white/10 px-3 py-1">{productLabel}</span>
            {!primaryCtaTo ? <span className="rounded-full bg-white/10 px-3 py-1">Coming soon</span> : null}
            {heroTags.map((tag) => (
              <span key={tag} className="rounded-full bg-white/10 px-3 py-1">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-black text-slate-900 md:text-4xl">
              {introTitle ?? `${productLabel} Calculator (Coming soon)`}
            </h2>
            <p className="mt-4 text-slate-600 md:text-lg">{introBody}</p>
          </div>

          {cards && (
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {cards.map((card, idx) => {
                const Icon = defaultFeatureIcons[idx] || Calculator;
                return (
                  <div
                    key={card.title}
                    className="rounded-xl border border-sky-100 bg-sky-50 p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-sky-200 hover:shadow-md"
                  >
                    <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-bold text-slate-900">{card.title}</h3>
                    <p className="mt-2 text-sm text-slate-600">{card.description}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="bg-sky-50 py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="grid gap-10 md:grid-cols-2 md:items-start">
            <div>
              <h2 className="text-3xl font-black text-slate-900 md:text-4xl">
                {howItWorksTitle ?? `How ${productLabel} works on Dynesourcer`}
              </h2>
              <p className="mt-4 text-slate-600">{howItWorksIntro}</p>

              <div className="mt-8 space-y-5">
                {stepList.map((it, idx) => {
                  const Icon = defaultStepIcons[idx] || Calculator;
                  return (
                    <div key={it.title} className="flex gap-4">
                      <div className="mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-sky-600 shadow-sm">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">
                          {idx + 1}. {it.title}
                        </h3>
                        <p className="mt-1 text-sm text-slate-600">{it.body}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border border-sky-100 bg-white p-6 shadow-sm">
              <h3 className="text-2xl font-black text-slate-900">{criteriaTitle}</h3>
              <p className="mt-3 text-slate-600">{criteriaIntro}</p>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {chips.map((chip) => (
                  <div
                    key={chip}
                    className="rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 text-sm font-medium text-slate-700"
                  >
                    {chip}
                  </div>
                ))}
              </div>

              <p className="mt-6 text-sm text-slate-600">
                Final criteria and calculator fields will match live lender data when {productLabel} goes live.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-sky-500 py-14 text-center text-white">
        <div className="mx-auto max-w-4xl px-4">
          <h3 className="text-3xl font-black md:text-4xl">{ctaTitle}</h3>
          <p className="mt-3 text-white/90">{ctaDescription}</p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            {ctaPrimary?.to && ctaPrimary?.label && (
              <Link
                to={ctaPrimary.to}
                className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 font-semibold text-sky-700 transition-all hover:-translate-y-0.5 hover:bg-slate-100 hover:shadow-lg"
              >
                {ctaPrimary.label} <ArrowRight className="h-4 w-4" />
              </Link>
            )}
            {ctaSecondary?.to && ctaSecondary?.label && (
              <Link
                to={ctaSecondary.to}
                className="inline-flex items-center gap-2 rounded-lg border border-white/40 bg-white/10 px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-white/20"
              >
                {ctaSecondary.label} <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}
